import {
  BuzzBootstrapInput,
  BuzzBootstrapResponse,
  BuzzChannel,
  BuzzThread,
  BuzzTimelineEvent,
} from '../types'

export class BuzzApiError extends Error {
  code: string
  status: number

  constructor(code: string, status: number, message: string) {
    super(message)
    this.name = 'BuzzApiError'
    this.code = code
    this.status = status
  }
}

interface BuzzApiClientOptions {
  baseUrl?: string
  fixtureAuth?: boolean
  fixtureToken?: string
  fetchImplementation?: typeof fetch
}

export class BuzzApiClient {
  private baseUrl: string
  private fixtureToken: string
  private fetchImplementation: typeof fetch

  constructor(options: BuzzApiClientOptions = {}) {
    this.baseUrl = (options.baseUrl || process.env.REACT_APP_BUZZ_API_BASE_URL || '/buzz-api/v1').replace(/\/$/, '')
    const fixtureAuth = options.fixtureAuth
      ?? process.env.REACT_APP_BUZZ_FIXTURE_AUTH === 'true'
    this.fixtureToken = fixtureAuth
      ? options.fixtureToken ?? process.env.REACT_APP_BUZZ_FIXTURE_TOKEN ?? ''
      : ''
    this.fetchImplementation = options.fetchImplementation || globalThis.fetch.bind(globalThis)
  }

  private async request<T>(
    path: string,
    init: RequestInit = {},
  ): Promise<T> {
    const headers: Record<string, string> = {
      'content-type': 'application/json; charset=utf-8',
      ...(init.headers as Record<string, string> || {}),
    }
    if (this.fixtureToken) headers.authorization = `Bearer ${this.fixtureToken}`
    const response = await this.fetchImplementation(`${this.baseUrl}${path}`, {
      ...init,
      headers,
      credentials: 'include',
    })
    const body = await response.json().catch(() => ({}))
    if (!response.ok) {
      throw new BuzzApiError(
        body?.error?.code || 'BRIDGE_REQUEST_FAILED',
        response.status,
        body?.error?.message || `Messaging request failed (${response.status}).`,
      )
    }
    return body as T
  }

  bootstrap(input: BuzzBootstrapInput): Promise<BuzzBootstrapResponse> {
    return this.request(
      `/organizations/${encodeURIComponent(input.organization.id)}/bootstrap`,
      { method: 'POST', body: JSON.stringify({}) },
    )
  }

  teamProjection(input: BuzzBootstrapInput): Promise<BuzzBootstrapResponse> {
    return this.request(
      `/organizations/${encodeURIComponent(input.organization.id)}/team`,
    )
  }

  createThread(organizationId: string, title: string, clientRequestId: string): Promise<BuzzThread> {
    return this.createThreadInChannel(organizationId, undefined, title, clientRequestId)
  }

  createThreadInChannel(
    organizationId: string,
    channelId: string | undefined,
    title: string,
    clientRequestId: string,
  ): Promise<BuzzThread> {
    return this.request(
      `/organizations/${encodeURIComponent(organizationId)}/threads`,
      {
        method: 'POST',
        headers: { 'idempotency-key': clientRequestId },
        body: JSON.stringify({ title, ...(channelId ? { channelId } : {}) }),
      },
    )
  }

  createChannel(
    organizationId: string,
    input: Pick<BuzzChannel, 'name' | 'description' | 'channelType' | 'visibility'>,
    clientRequestId: string,
  ): Promise<BuzzChannel> {
    return this.request(
      `/organizations/${encodeURIComponent(organizationId)}/channels`,
      {
        method: 'POST',
        headers: { 'idempotency-key': clientRequestId },
        body: JSON.stringify(input),
      },
    )
  }

  listEvents(organizationId: string, threadId: string): Promise<BuzzTimelineEvent[]> {
    return this.request(
      `/organizations/${encodeURIComponent(organizationId)}/threads/${encodeURIComponent(threadId)}/events`,
    )
  }

  publish(
    organizationId: string,
    threadId: string,
    content: string,
    mentions: Array<{ subjectType: 'agent'; subjectId: string }>,
    clientRequestId: string,
  ): Promise<BuzzTimelineEvent[]> {
    return this.request(
      `/organizations/${encodeURIComponent(organizationId)}/threads/${encodeURIComponent(threadId)}/events`,
      {
        method: 'POST',
        headers: { 'idempotency-key': clientRequestId },
        body: JSON.stringify({ content, mentions }),
      },
    )
  }
}
